(function () {
  'use strict';

  const SELECTOR = '.mmg-section';
  const FLIP_BACK_DELAY = 850;

  // Bump this whenever you relaunch as a new round/campaign — it invalidates
  // every returning visitor's cached session, same pattern as the other game.
  const CACHE_VERSION = 'r1';
  const SESSION_KEY = `mmg_session_id_${CACHE_VERSION}`;
  const UNLOCKED_KEY = `mmg_code_unlocked_${CACHE_VERSION}`;
  const CODE_KEY = `mmg_code_value_${CACHE_VERSION}`;

  function shuffle(items) {
    const array = [...items];
    for (let i = array.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  function getSessionId() {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `MMG-${Date.now().toString(36).toUpperCase()}-${Math.random()
        .toString(36)
        .slice(2, 9)
        .toUpperCase()}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  }

  function getDeviceType() {
    return window.matchMedia('(max-width: 767px)').matches ? 'Mobile' : 'Desktop';
  }

  function initializeGame(root) {
    if (!root || root.dataset.initialized === 'true') return;

    const dataElement = root.querySelector('[data-mmg-data]');
    if (!dataElement) return;

    let config;
    try {
      config = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('Memory Match: invalid section data.', error);
      return;
    }

    root.dataset.initialized = 'true';

    const products = (config.products || []).filter(
      (item) => item && item.name && item.image
    );

    const elements = {
      startScreen: root.querySelector('[data-mmg-start-screen]'),
      gameScreen: root.querySelector('[data-mmg-game-screen]'),
      levelCompleteScreen: root.querySelector('[data-mmg-level-complete-screen]'),
      resultScreen: root.querySelector('[data-mmg-result-screen]'),
      board: root.querySelector('[data-mmg-board]'),
      level: root.querySelector('[data-mmg-level]'),
      moves: root.querySelector('[data-mmg-moves]'),
      matches: root.querySelector('[data-mmg-matches]'),
      start: root.querySelector('[data-mmg-start]'),
      restart: root.querySelector('[data-mmg-restart]'),
      resetCache: root.querySelector('[data-mmg-reset-cache]'),
      nextLevel: root.querySelector('[data-mmg-next-level]'),
      levelCompleteTitle: root.querySelector('[data-mmg-level-complete-title]'),
      levelCompleteCopy: root.querySelector('[data-mmg-level-complete-copy]'),
      rewardLocked: root.querySelector('[data-mmg-reward-locked]'),
      rewardUnlocked: root.querySelector('[data-mmg-reward-unlocked]'),
      claim: root.querySelector('[data-mmg-claim]'),
      claimStatus: root.querySelector('[data-mmg-claim-status]'),
      code: root.querySelector('[data-mmg-code]'),
      copy: root.querySelector('[data-mmg-copy]'),
      modal: root.querySelector('[data-mmg-modal]'),
      modalCloseButtons: root.querySelectorAll('[data-mmg-modal-close]'),
      claimForm: root.querySelector('[data-mmg-claim-form]'),
      nameInput: root.querySelector('[data-mmg-name]'),
      phoneInput: root.querySelector('[data-mmg-phone]'),
      emailInput: root.querySelector('[data-mmg-email]'),
      formStatus: root.querySelector('[data-mmg-form-status]')
    };

    let currentLevel = 1;
    let moves = 0;
    let matches = 0;
    let pairsThisLevel = 0;
    let flippedCards = [];
    let locked = false;
    let lastFocusedElement = null;

    async function apiRequest(payload, expectResponse) {
      if (!config.trackingUrl) return null;

      const response = await fetch(config.trackingUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          ...payload,
          sessionId: getSessionId(),
          device: getDeviceType(),
          page: window.location.href
        })
      });

      if (!expectResponse) return null;
      return response.json();
    }

    function track(eventName, details) {
      apiRequest({ action: 'track', event: eventName, ...(details || {}) }, false).catch(() => {});
    }

    function showScreen(name) {
      elements.startScreen.hidden = name !== 'start';
      elements.gameScreen.hidden = name !== 'game';
      elements.levelCompleteScreen.hidden = name !== 'levelComplete';
      elements.resultScreen.hidden = name !== 'result';
    }

    function pairsForLevel(level) {
      const requested = level === 1 ? Number(config.level1Pairs || 4) : Number(config.level2Pairs || 6);
      return Math.max(2, Math.min(requested, products.length));
    }

    function buildLevel(level) {
      currentLevel = level;
      moves = 0;
      matches = 0;
      flippedCards = [];
      locked = false;

      pairsThisLevel = pairsForLevel(level);
      const chosenProducts = shuffle(products).slice(0, pairsThisLevel);
      const cardData = shuffle(
        chosenProducts.flatMap((product, index) => [
          { productIndex: index, product },
          { productIndex: index, product }
        ])
      );

      elements.level.textContent = `${level} / 2`;
      elements.moves.textContent = '0';
      elements.matches.textContent = `0 / ${pairsThisLevel}`;
      elements.board.innerHTML = '';

      cardData.forEach((card) => {
        const cardEl = document.createElement('div');
        cardEl.className = 'mmg-card';
        cardEl.dataset.productIndex = String(card.productIndex);

        cardEl.innerHTML = `
          <div class="mmg-card-inner">
            <div class="mmg-card-face mmg-card-back">
              ${config.logo ? `<img src="${config.logo}" alt="" loading="lazy">` : ''}
            </div>
            <div class="mmg-card-face mmg-card-front">
              <img src="${card.product.image}" alt="${card.product.name}" loading="lazy">
            </div>
          </div>
        `;

        cardEl.addEventListener('click', () => handleCardClick(cardEl));
        elements.board.appendChild(cardEl);
      });

      showScreen('game');
      track(level === 1 ? 'level1_started' : 'level2_started');
    }

    function handleCardClick(cardEl) {
      if (locked) return;
      if (cardEl.classList.contains('is-flipped') || cardEl.classList.contains('is-matched')) return;
      if (flippedCards.includes(cardEl)) return;

      cardEl.classList.add('is-flipped');
      flippedCards.push(cardEl);

      if (flippedCards.length < 2) return;

      moves += 1;
      elements.moves.textContent = String(moves);
      locked = true;

      const [first, second] = flippedCards;
      const isMatch = first.dataset.productIndex === second.dataset.productIndex;

      if (isMatch) {
        first.classList.add('is-matched');
        second.classList.add('is-matched');
        matches += 1;
        elements.matches.textContent = `${matches} / ${pairsThisLevel}`;
        flippedCards = [];
        locked = false;

        if (matches === pairsThisLevel) {
          window.setTimeout(() => handleLevelFinished(), 500);
        }
      } else {
        window.setTimeout(() => {
          first.classList.remove('is-flipped');
          second.classList.remove('is-flipped');
          flippedCards = [];
          locked = false;
        }, FLIP_BACK_DELAY);
      }
    }

    function handleLevelFinished() {
      if (currentLevel === 1) {
        elements.levelCompleteTitle.textContent = config.levelCompleteTitle || 'Level complete!';
        elements.levelCompleteCopy.textContent = config.levelCompleteCopy || '';
        showScreen('levelComplete');
        track('level1_completed', { score: `${moves} moves` });
      } else {
        showScreen('result');
        track('level2_completed', { score: `${moves} moves` });

        if (localStorage.getItem(UNLOCKED_KEY) === '1') {
          revealCode(localStorage.getItem(CODE_KEY));
        }
      }
    }

    // `code` is the unique, per-winner code returned by Apps Script/Shopify.
    // Falls back to the theme's static "discount_code" setting only if no
    // server code is available (e.g. the API call failed).
    function revealCode(code) {
      elements.rewardLocked.hidden = true;
      elements.rewardUnlocked.hidden = false;
      elements.code.textContent = code || config.discountCode || '';
      localStorage.setItem(UNLOCKED_KEY, '1');
      if (code) localStorage.setItem(CODE_KEY, code);
    }

    function resetGameState() {
      currentLevel = 1;
      moves = 0;
      matches = 0;
      flippedCards = [];
      locked = false;
      elements.rewardLocked.hidden = false;
      elements.rewardUnlocked.hidden = true;
      elements.claimStatus.textContent = '';
      elements.code.textContent = '—';
      closeModal();
      if (elements.claimForm) elements.claimForm.reset();
      if (elements.formStatus) elements.formStatus.textContent = '';
      showScreen('start');
    }

    // Only present in the theme editor preview (gated by request.design_mode
    // in the liquid file) — clears this browser's session/unlock state so
    // staff can replay repeatedly while testing.
    function clearGameCache() {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(UNLOCKED_KEY);
      localStorage.removeItem(CODE_KEY);
      window.location.reload();
    }

    // ---------- Claim popup ----------

    function openModal() {
      if (!elements.modal) return;
      lastFocusedElement = document.activeElement;
      elements.modal.hidden = false;
      document.body.classList.add('mmg-modal-open');
      if (elements.formStatus) elements.formStatus.textContent = '';
      window.setTimeout(() => {
        if (elements.nameInput) elements.nameInput.focus();
      }, 0);
      document.addEventListener('keydown', handleModalKeydown);
    }

    function closeModal() {
      if (!elements.modal) return;
      elements.modal.hidden = true;
      document.body.classList.remove('mmg-modal-open');
      document.removeEventListener('keydown', handleModalKeydown);
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }
    }

    function handleModalKeydown(event) {
      if (event.key === 'Escape') closeModal();
    }

    function requestClaim() {
      if (localStorage.getItem(UNLOCKED_KEY) === '1') {
        revealCode(localStorage.getItem(CODE_KEY));
        return;
      }
      openModal();
    }

    async function handleClaimFormSubmit(event) {
      event.preventDefault();

      const name = elements.nameInput ? elements.nameInput.value.trim() : '';
      const phone = elements.phoneInput ? elements.phoneInput.value.trim() : '';
      const email = elements.emailInput ? elements.emailInput.value.trim() : '';

      if (!name || !phone) {
        elements.formStatus.textContent =
          config.requiredFieldsText || 'Please enter your name and phone number.';
        return;
      }

      const submitButton = elements.claimForm.querySelector('[data-mmg-form-submit]');
      if (submitButton) submitButton.disabled = true;
      elements.formStatus.textContent = config.claimingText || 'Unlocking your code…';

      try {
        // Note: no `code` is sent to the server anymore — Apps Script now
        // generates a unique Shopify discount code per winner and returns
        // it in `result.code`.
        const result = await apiRequest(
          {
            action: 'claim',
            event: 'lead_captured',
            name,
            phone,
            email
          },
          true
        );

        if (!result || !result.success) {
          throw new Error((result && result.message) || 'Could not unlock your code.');
        }

        closeModal();
        revealCode(result.code);
        elements.claimStatus.textContent = config.claimSuccessText || 'Code unlocked!';
      } catch (error) {
        elements.formStatus.textContent =
          error.message || config.claimErrorText || 'Something went wrong. Please try again.';
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    }

    async function copyCode() {
      const value = elements.code.textContent.trim();
      if (!value || value === '—') return;

      try {
        await navigator.clipboard.writeText(value);
      } catch (error) {
        const temporaryInput = document.createElement('textarea');
        temporaryInput.value = value;
        temporaryInput.setAttribute('readonly', '');
        temporaryInput.style.position = 'fixed';
        temporaryInput.style.opacity = '0';
        document.body.appendChild(temporaryInput);
        temporaryInput.select();
        document.execCommand('copy');
        temporaryInput.remove();
      }

      const originalLabel = elements.copy.textContent;
      elements.copy.textContent = config.copiedText || 'Copied! ✓';
      window.setTimeout(() => {
        elements.copy.textContent = originalLabel;
      }, 1800);
    }

    // ---------- Wire up events ----------

    elements.start.addEventListener('click', () => {
      if (products.length < 2) {
        window.alert('Please upload at least 2 product cards in the Theme Editor.');
        return;
      }
      buildLevel(1);
    });

    elements.nextLevel.addEventListener('click', () => buildLevel(2));
    elements.restart.addEventListener('click', resetGameState);
    elements.claim.addEventListener('click', requestClaim);
    elements.copy.addEventListener('click', copyCode);

    if (elements.resetCache) {
      elements.resetCache.addEventListener('click', () => {
        const confirmed = window.confirm(
          'This clears your saved progress and unlocked code so you can play again as a new session. Continue?'
        );
        if (confirmed) clearGameCache();
      });
    }

    if (elements.claimForm) {
      elements.claimForm.addEventListener('submit', handleClaimFormSubmit);
    }

    elements.modalCloseButtons.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    if (elements.modal) {
      elements.modal.addEventListener('click', (event) => {
        if (event.target === elements.modal) closeModal();
      });
    }

    showScreen('start');
  }

  function initializeAll(container) {
    const scope = container || document;
    if (scope.matches && scope.matches(SELECTOR)) {
      initializeGame(scope);
      return;
    }
    scope.querySelectorAll(SELECTOR).forEach(initializeGame);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document));
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();